import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI, soCreateAPI, vendorAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, CurrencyInr, Eye, ArrowUUpLeft, CaretDown, PencilSimple, UploadSimple,
} from '@phosphor-icons/react';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import ItemSearchPicker from '@/components/ItemSearchPicker';

const blankLine = () => ({
  item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0,
  discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [],
});

// Multi-select dropdown of available serials for an item at the chosen branch
function SerialSelect({ itemId, clinicId, value = [], onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !itemId) return;
    setLoading(true);
    const params = new URLSearchParams({ item_id: itemId, status: 'available', limit: '500' });
    if (clinicId) params.set('clinic_id', clinicId);
    api.get(`/serials/by-item?${params.toString()}`)
      .then(r => setSerials((r.data.serials || []).map(s => s.serial_number)))
      .catch(() => setSerials([]))
      .finally(() => setLoading(false));
  }, [open, itemId, clinicId]);
  const toggle = (sn) => onChange(value.includes(sn) ? value.filter(x => x !== sn) : [...value, sn]);
  const options = Array.from(new Set([...(value || []), ...serials]));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="w-full h-8 text-xs border border-[#E2E8F0] rounded px-2 flex items-center justify-between bg-white hover:bg-[#F8FAFC]">
          <span className="truncate text-left">{value.length ? `${value.length} serial(s)` : 'Select serials'}</span>
          <CaretDown size={12} className="text-[#64748B] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        {!itemId ? (
          <div className="text-xs text-[#94A3B8] py-2">Pick an item first</div>
        ) : loading ? (
          <div className="text-xs text-[#64748B] py-2">Loading…</div>
        ) : options.length === 0 ? (
          <div className="text-[11px] text-[#94A3B8] py-2 italic">No available serials for this item at this branch.</div>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {options.map(sn => (
              <label key={sn} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#F8FAFC] px-1.5 py-1 rounded" data-testid={`${testid}-opt-${sn}`}>
                <input type="checkbox" checked={value.includes(sn)} onChange={() => toggle(sn)} />
                <span className="truncate">{sn}</span>
              </label>
            ))}
          </div>
        )}
        {value.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1 border-t border-[#E2E8F0] pt-2">
            {value.map(sn => <span key={sn} className="bg-red-50 text-red-700 text-[10px] px-1.5 py-0.5 rounded">{sn}</span>)}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function VendorCreditsPage() {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorBills, setVendorBills] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextNumber, setNextNumber] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    vendor_id: '', vendor_name: '', branch_id: '', vendor_bill_id: '', bill_number: '',
    credit_number: '', credit_date: new Date().toISOString().split('T')[0], reason: '', notes: '',
  });
  const [lineItems, setLineItems] = useState([blankLine()]);

  const loadCredits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await billingOpsAPI.listVendorCredits({ page, search, branch_id: branchFilter });
      setCredits(data.credits || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, branchFilter]);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  useEffect(() => {
    (async () => {
      const [brRes, itemsRes, venRes, billRes] = await Promise.allSettled([
        soCreateAPI.branches(),
        soCreateAPI.items({ per_page: 2000 }),
        vendorAPI.getAll(),
        billingOpsAPI.listVendorBills({ per_page: 200 }),
      ]);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
      if (venRes.status === 'fulfilled') setVendors(venRes.value.data.vendors || []);
      if (billRes.status === 'fulfilled') setVendorBills(billRes.value.data.bills || []);
    })();
  }, []);

  // Refresh the auto credit-number preview when the branch changes
  useEffect(() => {
    if (!showCreate || !form.branch_id) { setNextNumber(''); return; }
    billingOpsAPI.vendorCreditNextNumber(form.branch_id)
      .then(({ data }) => setNextNumber(data.next_number || ''))
      .catch(() => setNextNumber(''));
  }, [showCreate, form.branch_id]);

  const resetForm = () => {
    setForm({
      vendor_id: '', vendor_name: '', branch_id: '', vendor_bill_id: '', bill_number: '',
      credit_number: '', credit_date: new Date().toISOString().split('T')[0], reason: '', notes: '',
    });
    setLineItems([blankLine()]);
    setNextNumber('');
    setEditingId(null);
  };

  const openEdit = (credit) => {
    setForm({
      vendor_id: credit.vendor_id || '', vendor_name: credit.vendor_name || '',
      branch_id: credit.branch_id || '', vendor_bill_id: credit.vendor_bill_id || '',
      bill_number: credit.bill_number || '', credit_number: credit.credit_number || '',
      credit_date: credit.credit_date || new Date().toISOString().split('T')[0],
      reason: credit.reason || '', notes: credit.notes || '',
    });
    setLineItems((credit.line_items || []).map(li => ({
      item_id: li.item_id || '', name: li.name || '', product_code: li.product_code || '',
      hsn_sac: li.hsn_sac || '', quantity: li.quantity || 1, rate: li.rate || 0,
      discount_percent: li.discount_percent || 0, discount_amount: li.discount_amount || 0,
      tax_percent: li.tax_percent || 0, serial_numbers: li.serial_numbers || [],
    })) || [blankLine()]);
    setEditingId(credit.id);
    setShowDetail(null);
    setShowCreate(true);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await billingOpsAPI.bulkUploadVendorCredits(file);
      toast.success(`Imported ${data.inserted} new vendor credit(s) (${data.parsed} parsed, ${data.skipped_existing} already existed)`);
      setPage(1);
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import vendor credits');
    } finally { setUploading(false); }
  };

  // Prefill everything from a chosen vendor bill (the "from bill" flow)
  const loadFromBill = (billId) => {
    const bill = vendorBills.find(b => b.id === billId);
    if (!bill) return;
    setForm(f => ({
      ...f,
      vendor_id: bill.vendor_id || '',
      vendor_name: bill.vendor_name || '',
      branch_id: bill.branch_id || bill.location_id || '',
      vendor_bill_id: bill.id,
      bill_number: bill.bill_number || '',
    }));
    const lis = (bill.line_items || []).map(li => ({
      item_id: li.item_id || '', name: li.name || '', product_code: li.product_code || '',
      hsn_sac: li.hsn_sac || '', quantity: li.quantity || 1, rate: li.rate || 0,
      discount_percent: 0, discount_amount: 0, tax_percent: li.tax_percent || 0,
      serial_numbers: li.serial_numbers || [],
    }));
    setLineItems(lis.length ? lis : [blankLine()]);
  };

  const selectCatalogItem = (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      hsn_sac: item.hsn_sac_code || '', rate: item.purchase_price || item.mrp || 0, tax_percent: gstRate,
    } : li));
  };

  const updateLineItem = (idx, field, value) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  const addLineItem = () => setLineItems(prev => [...prev, blankLine()]);
  const removeLineItem = (idx) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== idx)); };

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const estTotal = lineItems.reduce((sum, li) => {
    const line = (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0);
    const tax = line * ((parseFloat(li.tax_percent) || 0) / 100);
    return sum + line + tax;
  }, 0);

  const handleCreate = async () => {
    if (!form.vendor_name) { toast.error('Vendor is required'); return; }
    if (!form.branch_id) { toast.error('Branch is required'); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }

    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id || '',
        vendor_name: form.vendor_name,
        branch_id: form.branch_id,
        credit_number: form.credit_number || '',
        vendor_bill_id: form.vendor_bill_id || '',
        bill_number: form.bill_number || '',
        credit_date: form.credit_date,
        reason: form.reason,
        notes: form.notes,
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code, hsn_sac: li.hsn_sac,
          quantity: parseFloat(li.quantity) || 1, rate: parseFloat(li.rate) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0,
          discount_amount: parseFloat(li.discount_amount) || 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
          serial_numbers: li.serial_numbers || [],
        })),
      };
      const { data } = editingId
        ? await billingOpsAPI.editVendorCredit(editingId, payload)
        : await billingOpsAPI.createVendorCredit(payload);
      toast.success(editingId
        ? `Vendor Credit ${data.credit_number} updated`
        : `Vendor Credit ${data.credit_number} created — ${data.serials_returned || 0} serial(s) returned`);
      setShowCreate(false);
      resetForm();
      loadCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save vendor credit');
    } finally { setSaving(false); }
  };

  const handleDelete = async (credit) => {
    if (!window.confirm(`Delete vendor credit "${credit.credit_number}"?\n\nThis will restore the returned stock (and serials) back to inventory. This cannot be undone.`)) return;
    try {
      const { data } = await billingOpsAPI.deleteVendorCredit(credit.id);
      toast.success(data?.message || 'Vendor credit deleted');
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor credit');
    }
  };

  return (
    <div className="p-6" data-testid="vendor-credits-page">
      <PageHeader
        title="Vendor Credit"
        subtitle="Credit notes for stock returned to OEMs / vendors"
        actions={
          <div className="flex items-center gap-2">
            <label className="inline-flex">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} data-testid="vc-upload-input" />
              <span className={`inline-flex items-center px-3 py-2 text-sm rounded-md border border-[#E2E8F0] cursor-pointer hover:bg-[#F8FAFC] ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="vc-upload-btn">
                <UploadSimple size={16} className="mr-2" /> {uploading ? 'Importing...' : 'Upload Excel'}
              </span>
            </label>
            <Button className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="vc-create-btn">
              <Plus size={16} className="mr-2" /> New Vendor Credit
            </Button>
          </div>
        }
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by credit #, vendor or bill..." className="pl-9" data-testid="vc-search" />
        </div>
        <SearchableSelect value={branchFilter || ''} onChange={v => { setBranchFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Branches' }, ...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]}
          placeholder="All Branches" testid="vc-branch-filter" buttonClassName="w-[200px]" />
        <span className="text-xs text-[#64748B]">{total.toLocaleString('en-IN')} vendor credits</span>
      </div>

      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="vc-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
              <th className="text-left p-3">Credit #</th>
              <th className="text-left p-3">Vendor</th>
              <th className="text-left p-3">Branch</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Against Bill</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : credits.length > 0 ? credits.map(c => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] cursor-pointer" onClick={() => setShowDetail(c)} data-testid={`vc-row-${c.id}`}>
                <td className="p-3 font-medium text-[#0F4C81]">{c.credit_number}</td>
                <td className="p-3">{c.vendor_name}</td>
                <td className="p-3">{c.branch_name}</td>
                <td className="p-3">{c.credit_date}</td>
                <td className="p-3 text-[#64748B]">{c.bill_number || '—'}</td>
                <td className="p-3 text-right font-medium text-red-700">{fmt(c.grand_total)}</td>
                <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowDetail(c)} title="View" data-testid={`vc-view-${c.id}`}><Eye size={14} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(c)} title="Edit" data-testid={`vc-edit-${c.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(c)} title="Delete" data-testid={`vc-delete-${c.id}`}><Trash size={14} /></Button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No vendor credits yet. Create one when you return stock to an OEM.</td></tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-[#E2E8F0]">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-xs text-[#64748B]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CurrencyInr size={18} /> Vendor Credit: {showDetail?.credit_number}</DialogTitle>
            <DialogDescription>Details of stock returned to the vendor and the GST/amount credited.</DialogDescription>
          </DialogHeader>
          {showDetail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><span className="text-[#64748B]">Vendor:</span> <strong>{showDetail.vendor_name}</strong></div>
                <div><span className="text-[#64748B]">Branch:</span> {showDetail.branch_name}</div>
                <div><span className="text-[#64748B]">Date:</span> {showDetail.credit_date}</div>
                <div><span className="text-[#64748B]">Against Bill:</span> {showDetail.bill_number || '—'}</div>
                <div><span className="text-[#64748B]">Grand Total:</span> <strong className="text-red-700">{fmt(showDetail.grand_total)}</strong></div>
                <div><span className="text-[#64748B]">GST:</span> {fmt(showDetail.total_tax)}</div>
              </div>
              {showDetail.reason && <p className="text-xs text-[#64748B]"><strong>Reason:</strong> {showDetail.reason}</p>}
              {showDetail.line_items?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[#64748B] mb-2">Returned Items</h4>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[#F8FAFC]">
                      <th className="text-left p-2">Item</th><th className="text-center p-2">Qty</th>
                      <th className="text-right p-2">Rate</th><th className="text-right p-2">Tax</th>
                      <th className="text-right p-2">Total</th><th className="text-left p-2">Serial #s</th>
                    </tr></thead>
                    <tbody>
                      {showDetail.line_items.map((li, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-2">{li.name}</td>
                          <td className="p-2 text-center">{li.quantity}</td>
                          <td className="p-2 text-right">{fmt(li.rate)}</td>
                          <td className="p-2 text-right">{li.tax_percent || 0}%</td>
                          <td className="p-2 text-right font-medium">{fmt(li.total)}</td>
                          <td className="p-2 text-[10px]">
                            <div className="flex flex-wrap gap-1">
                              {(li.serial_numbers || []).map(sn => <SerialHistoryLink key={sn} serialNumber={sn} className="bg-red-50 text-red-700" />)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {showDetail.notes && <p className="text-xs text-[#64748B]"><strong>Notes:</strong> {showDetail.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUUpLeft size={18} /> {editingId ? 'Edit' : 'New'} Vendor Credit</DialogTitle>
            <DialogDescription>Record stock returned to an OEM/vendor — this reduces inventory and the amount payable, and reverses input GST.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] p-3">
              <label className="text-xs font-medium text-[#0F172A] block mb-1">Load from Vendor Bill (optional)</label>
              <Select value={form.vendor_bill_id || 'none'} onValueChange={v => { if (v === 'none') { setForm(f => ({ ...f, vendor_bill_id: '', bill_number: '' })); } else { loadFromBill(v); } }}>
                <SelectTrigger data-testid="vc-from-bill"><SelectValue placeholder="Standalone (no bill) — or pick a bill to prefill" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Standalone (no bill)</SelectItem>
                  {vendorBills.map(b => <SelectItem key={b.id} value={b.id}>{b.bill_number} — {b.vendor_name} ({fmt(b.grand_total)})</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#64748B] mt-1">Pick a bill to auto-fill the vendor, branch and items you're returning — or leave as Standalone and enter them manually.</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Vendor *</label>
                <Select value={form.vendor_name} onValueChange={v => { const ven = vendors.find(x => x.name === v); setForm(f => ({ ...f, vendor_name: v, vendor_id: ven?.vendor_id || '' })); }}>
                  <SelectTrigger data-testid="vc-vendor"><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {vendors.map(v => <SelectItem key={v.vendor_id} value={v.name}>{v.display_name || v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Branch *</label>
                <SearchableSelect value={form.branch_id} onChange={v => setForm(f => ({ ...f, branch_id: v }))} placeholder="Select branch..." testid="vc-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Credit Date</label>
                <Input type="date" value={form.credit_date} onChange={e => setForm(f => ({ ...f, credit_date: e.target.value }))} data-testid="vc-date" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Credit Number</label>
                <Input value={form.credit_number} onChange={e => setForm(f => ({ ...f, credit_number: e.target.value }))} placeholder={nextNumber ? `e.g. ${nextNumber}` : 'Enter credit number'} data-testid="vc-number-input" />
                <p className="text-[10px] text-[#64748B] mt-1">Enter manually{nextNumber ? `, or leave blank to auto-generate (${nextNumber})` : ''}</p>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-[#64748B] block mb-1">Reason</label>
                <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Defective units returned to OEM" data-testid="vc-reason" />
              </div>
            </div>

            <h4 className="text-xs font-semibold text-[#0F172A]">Items Returned</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vc-items-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                    <th className="text-left p-2 w-[220px]">Item</th>
                    <th className="text-center p-2 w-[60px]">Qty</th>
                    <th className="text-right p-2 w-[90px]">Rate</th>
                    <th className="text-center p-2 w-[60px]">Tax%</th>
                    <th className="text-left p-2 w-[150px]">Serial #s</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`vc-line-${idx}`}>
                      <td className="p-2">
                        <ItemSearchPicker items={catalogItems} value={li.name} onPick={(item) => selectCatalogItem(idx, item.id)} testid={`vc-item-select-${idx}`} />
                      </td>
                      <td className="p-2"><Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs" data-testid={`vc-qty-${idx}`} /></td>
                      <td className="p-2"><Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`vc-rate-${idx}`} /></td>
                      <td className="p-2"><Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`vc-tax-${idx}`} /></td>
                      <td className="p-2"><SerialSelect itemId={li.item_id} clinicId={form.branch_id} value={li.serial_numbers || []} onChange={(arr) => updateLineItem(idx, 'serial_numbers', arr)} testid={`vc-serial-${idx}`} /></td>
                      <td className="p-2">{lineItems.length > 1 && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}><Trash size={14} /></Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addLineItem} data-testid="vc-add-item"><Plus size={14} className="mr-1" /> Add Item</Button>
              <span className="text-sm text-[#64748B]">Est. Credit Total: <strong className="text-red-700">{fmt(estTotal)}</strong></span>
            </div>

            <div>
              <label className="text-xs text-[#64748B] block mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes" data-testid="vc-notes" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="vc-submit-btn">
                <FloppyDisk size={16} className="mr-2" />{saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Create Vendor Credit')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
